import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Tooltip,
  TreeSelect
} from "antd";
import { message } from "utils/ciq-message";
import { useForm } from "antd/lib/form/Form";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  areArrayObjectsEqualByKey,
  compareObjectPropertyValues,
  compareStringArrays
} from "utils/utils";
import {
  UnitType,
  getTreeFromMeasuremntList
} from "pages/material-details/material-details-tab/units-tree";
import { QUERY_MASTER_UNITS } from "services/graphQL/queries";
import { useProjectParticipants } from "hooks/project-participants";
import SelectSearchNotFoundContent from "components/widgets/select-search-notfound-content";
import CreateSpecSectionModal from "components/submittal-details/create-spec-section";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import UserTag from "components/user-tag/user-tag";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import TextArea from "antd/lib/input/TextArea";
import { useShippingLocation } from "hooks/material";
import { LocationSearchNotFoundMessages } from "components/widgets/location-search-not-found-messages";
import { useLocationBreakdownStructure } from "hooks/location-breakdown-structure";
import LBSSelectField from "components/lbs-select-field/lbs-select-field";
import { useParams } from "react-router";
import useProjectTemplateListHook from "pages/preconstruction-details/components/workflow-template-dropdown/use-project-template-list";
import { FeatureTypes } from "entity-app/constants";
import { TMaterialLog } from "pages/material-log-feature/model";
import SelectFeatureSpecSections from "components/feature-spec-section-dropdown/feature-spec-sections";
import { MUTATION_BULK_EDIT_FEATURE_INSTANCES } from "entity-app/graphQL/ciq-feature-mutations";
import { gql } from "@apollo/client";
import RedirectIcon from "components/svg-icons/redirect-icon";
import CustomFields from "components/custom-fields";
import { Link } from "react-router-dom";
import {
  AcceptanceStatus,
  EWorkflowStatusDataBlock,
  SidepanelStyles
} from "../../constants";
import DisplayNameListPopover from "./display-name-list";

interface SelectedRowItem {
  id: string;
  name: string;
  spec_sections: Array<string>;
  description: string | null;
  responsible_contractor: string | null;
  assignee: string | null;
  assignee_unregistered: string | null;
  gc_representative: string | null;
  material_tag: string | null;
  status: string;
  quantity: number | null;
  quantity_unit: string | null;
  material_size: string | null;
  material_size_unit: string | null;
  manufacturer: string | null;
  part_number: string;
  workflow_template_id: string;
  float: number;
  shipping_location: {
    id: string;
    label: string;
  };
  purchase_order: string | null;
  locations: {
    id: string;
    name: string;
    path: string;
    parent_id: string;
  }[];
}

type TEditMaterial = {
  spec_sections: Array<string>;
  quantity: string | undefined;
  quantity_unit_id: any | undefined;
  responsible_contractor: string | undefined | null;
  assignee: string | undefined | null;
  assignee_unregistered: string | undefined | null;
  gc_representative: string | undefined | null;
  workflow_template_id: string | undefined | null;
  float: any | undefined;
  description: string | undefined;
  tag_name: string | undefined;
  manufacturer: string | undefined;
  material_size: string | undefined;
  size_unit_id: string | undefined;
  part_number: string | undefined;
  place_id: string | undefined;
  purchase_order: string | undefined;
  location_ids: string[] | undefined;
};

export type EditMaterialLogs = {
  onUpdateDone: any;
  onCancelClick: any;
  selectedRows: TMaterialLog[];
  showEdit: boolean;
  setShowEdit: any;
};

function BulkFeatureEditMaterialDetails(props: Readonly<EditMaterialLogs>) {
  const { onUpdateDone, onCancelClick, selectedRows, showEdit, setShowEdit } =
    props;
  const { projectId } = useParams() as any;
  const [form] = useForm();
  const createSpecRef = useRef<any>(null);

  const [isDiff, setIsDiff] = useState(false);
  const [initialData, setInitialData] = useState<TEditMaterial>();
  const [materialListIds, setMaterialListIds] = useState<{
    NotStarted: Array<{
      id: string;
      name: string;
      workflow_instance_id?: string;
    }>;
    Started: Array<{ id: string; name: string; workflow_instance_id?: string }>;
  }>({
    NotStarted: [],
    Started: []
  });
  const { Option } = Select;

  const [editMaterialData, setEditMaterialData] = useState<TEditMaterial>({
    spec_sections: [],
    quantity: undefined,
    quantity_unit_id: undefined,
    responsible_contractor: undefined,
    assignee: undefined,
    assignee_unregistered: undefined,
    gc_representative: undefined,
    workflow_template_id: undefined,
    float: undefined,
    description: undefined,
    tag_name: undefined,
    manufacturer: undefined,
    material_size: undefined,
    size_unit_id: undefined,
    part_number: undefined,
    place_id: undefined,
    purchase_order: undefined,
    location_ids: undefined
  });

  const [customFieldChanges, setCustomFieldChanges] = useState<
    Record<string, any>
  >({});

  const onCustomFieldChange = (fieldName: string, value: any) => {
    setCustomFieldChanges((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const [inProgress, setInProgress] = useState(false);
  const {
    columnHeaders: { materialHeaderMap, materialCustomColumns },
    tokenRetrievalState,
    gqlClientForProject,
    isFeatureFlagEnabled
  }: TProjectContext = useContext(ProjectContext);

  const {
    options: MDBTemplatesDropdownOptions,

    loading: TemplateLoading
  } = useProjectTemplateListHook({ featureId: FeatureTypes.MATERIALS });

  const isLBSFeatureEnabled = isFeatureFlagEnabled(
    "LOCATION_BREAKDOWN_STRUCTURE"
  );

  const { data: allUnits } = useCIQQuery<{
    material_quantity_unit: Array<UnitType>;
    material_size_unit: Array<UnitType>;
  }>(QUERY_MASTER_UNITS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const { projectParticipants } = useProjectParticipants();
  const [showCreateSpecSectionModal, setShowCreateSpecSectionModal] =
    useState(false);

  const [bulkUpdateFeatureInstances] = useCIQMutation(
    gql(MUTATION_BULK_EDIT_FEATURE_INSTANCES),
    {
      client: gqlClientForProject
    }
  );

  const {
    searchLocation,
    fetching,
    selectOptions: shippingLocationOptionResults,
    searchedString
  } = useShippingLocation({
    gqlClientForProject,
    projectToken: tokenRetrievalState?.token
  });

  const { LBSOptionsMaster, LBSDataInTreeFormat } =
    useLocationBreakdownStructure(gqlClientForProject, projectId, true);

  const quantityUnitTreeOptions = useMemo(
    () =>
      allUnits
        ? getTreeFromMeasuremntList(allUnits.material_quantity_unit)
        : [],
    [allUnits]
  );

  const formattedSelectedRows = useMemo(() => {
    return selectedRows.map((x) => ({
      id: x.id,
      name: x.title,
      spec_sections: x.spec_section?.map((s) => s.id),
      description: x.description,
      responsible_contractor: x.responsible_contractor?.id,
      assignee: x.assignee_user?.id,
      assignee_unregistered: x.assignee_unregistered,
      gc_representative: x.gc_representative?.id,
      material_tag: x.tag_name,
      status: x.workflow_status,
      quantity: x.quantity,
      quantity_unit: x.quantity_unit_id,
      material_size: x.material_size,
      material_size_unit: allUnits?.material_quantity_unit.find(
        (u) => u.unit === x.material_size_unit
      )?.id,
      manufacturer: x.manufacturer,
      part_number: x.part_number,
      workflow_template_id: x.template?.id,
      workflow_instance_id: x.date_block?.id,
      float: x.pre_WF_start_float,
      shipping_location: x.shipping_location,
      purchase_order: x.purchase_order,
      locations: x.locations,
      place_id: x.place_id
    }));
  }, [allUnits?.material_quantity_unit, selectedRows]);

  const syntheticFeatureInstanceData = useMemo(() => {
    return {
      id: selectedRows[0]?.id || "",
      _custom_fields: { ...customFieldChanges }
    };
  }, [selectedRows, customFieldChanges]);

  useEffect(() => {
    if (initialData) {
      const compare = compareObjectPropertyValues(
        [initialData, editMaterialData],
        Object.getOwnPropertyNames(initialData).map((x) => ({
          field: x
        }))
      );

      const hasCustomFieldChanges = Object.keys(customFieldChanges).length > 0;
      setIsDiff(compare.diffFoundSummary || hasCustomFieldChanges);
    }
  }, [initialData, editMaterialData, customFieldChanges]);

  useEffect(() => {
    if (!formattedSelectedRows.length) {
      setShowEdit(false);
      return;
    }

    const compareFields = [
      "spec_sections",
      "quantity",
      "quantity_unit",
      "responsible_contractor",
      "assignee",
      "assignee_unregistered",
      "gc_representative",
      "workflow_template_id",
      "float",
      "description",
      "material_tag",
      "manufacturer",
      "material_size",
      "material_size_unit",
      "part_number",
      "place_id",
      "purchase_order"
    ];

    const firstElement = {} as any;
    compareFields.forEach((field) => {
      firstElement[field] =
        formattedSelectedRows[0][field as keyof SelectedRowItem];
    });

    let locationInitialIds: string[] = [];

    if (formattedSelectedRows.length > 1) {
      const compare = compareObjectPropertyValues(
        formattedSelectedRows,
        compareFields.map((x) => ({
          field: x
        }))
      );

      compare.fieldsAsArray.forEach((x) => {
        if (x.diffFound) firstElement[x.field] = undefined;
      });

      if (isLBSFeatureEnabled) {
        const areLocationsSame = areArrayObjectsEqualByKey(
          formattedSelectedRows.map((x) => x.locations),
          "id"
        );

        if (areLocationsSame) {
          locationInitialIds = formattedSelectedRows
            .map((x) => x.locations || [])
            .flat()
            .map((x) => x.id);

          locationInitialIds = [...new Set(locationInitialIds)];
        }
      }
    }

    const gcRepresentative = projectParticipants?.gcReviewers?.length
      ? firstElement?.gc_representative
      : "";

    const intialValue = {
      spec_sections: firstElement.spec_sections,
      quantity: firstElement.quantity,
      quantity_unit_id: firstElement.quantity_unit,
      responsible_contractor: firstElement.responsible_contractor,
      assignee: firstElement.assignee,
      assignee_unregistered: firstElement.assignee_unregistered,
      gc_representative: gcRepresentative,
      workflow_template_id: firstElement.workflow_template_id,
      float: firstElement.float,
      description: firstElement.description,
      tag_name: firstElement.material_tag,
      manufacturer: firstElement.manufacturer,
      material_size: firstElement.material_size,
      size_unit_id: firstElement.material_size_unit,
      part_number: firstElement.part_number,
      place_id: firstElement.place_id,
      purchase_order: firstElement.purchase_order
    } as TEditMaterial;

    if (isLBSFeatureEnabled) {
      intialValue.location_ids = locationInitialIds;
    }

    setInitialData(intialValue);
    setEditMaterialData(intialValue);

    const NotStartedStatus = EWorkflowStatusDataBlock.WF_NOT_STARTED;
    const workflowStartedSubmittals = formattedSelectedRows
      .filter((x: any) => x?.status !== NotStartedStatus)
      .map((x) => ({
        id: x.id,
        name: x.name,
        workflow_instance_id: x.workflow_instance_id
      }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
    const workflowNotStartedSubmittals = formattedSelectedRows
      .filter((x: any) => x?.status === NotStartedStatus)
      .map((x) => ({
        id: x.id,
        name: x.name,
        workflow_instance_id: x.workflow_instance_id
      }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    setMaterialListIds({
      NotStarted: workflowNotStartedSubmittals,
      Started: workflowStartedSubmittals
    });
  }, [
    formattedSelectedRows,
    isLBSFeatureEnabled,
    projectParticipants,
    setShowEdit
  ]);

  const materialTemplateSelectField = useMemo(() => {
    // materialListIds state is always initialized/set with array NotStarted/Started -> `|| []` fallback is dead
    /* v8 ignore next */
    const notStartedList = materialListIds?.NotStarted || [];
    // materialListIds state is always initialized/set with array NotStarted/Started -> `|| []` fallback is dead
    /* v8 ignore next */
    const startedList = materialListIds?.Started || [];

    const allMaterials = [...notStartedList, ...startedList].sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "")
    );

    const wfStartedMessage = (
      <div className="text-xs mb-2">
        This field will only be updated in
        <DisplayNameListPopover list={notStartedList} placement="left">
          {notStartedList.length}
        </DisplayNameListPopover>
        out of
        <DisplayNameListPopover list={allMaterials} placement="left">
          {allMaterials.length}
        </DisplayNameListPopover>
        Materials for which Workflow has not started.
      </div>
    );

    return (
      <div>
        {materialListIds.NotStarted.length > 0 && (
          <div className="space-y-3">
            <Form.Item
              label={materialHeaderMap?.date_block_template_name}
              data-testid="workflow-template-form-item"
            >
              {materialListIds.Started.length > 0 &&
                materialListIds.NotStarted.length > 0 &&
                wfStartedMessage}
              <Select
                optionFilterProp="label"
                value={editMaterialData.workflow_template_id}
                onChange={(value) => {
                  setEditMaterialData((pre) => ({
                    ...pre,
                    workflow_template_id: value
                  }));
                }}
                options={MDBTemplatesDropdownOptions}
                data-testid="workflow-template-dropdown"
                labelRender={(labelProps) => (
                  <span data-testid="workflow-template-value">
                    {labelProps.label}
                  </span>
                )}
                loading={TemplateLoading}
              />
            </Form.Item>
          </div>
        )}
      </div>
    );
  }, [
    MDBTemplatesDropdownOptions,
    TemplateLoading,
    editMaterialData.workflow_template_id,
    materialHeaderMap?.date_block_template_name,
    materialListIds.NotStarted,
    materialListIds.Started
  ]);

  const optionLabel = (user: any, selectedUserId: any) => {
    if (
      user.status_id === AcceptanceStatus.DEACTIVATED &&
      user.id !== selectedUserId
    )
      return "";

    const selectedAndInactive =
      user.status_id === AcceptanceStatus.DEACTIVATED &&
      user.id === selectedUserId;

    const inactiveTag = user.status_id === AcceptanceStatus.DEACTIVATED && (
      <div className="absolute flex w-full items-center justify-end pr-3 -mt-[1px]">
        <UserTag label="Inactive" />
      </div>
    );

    return (
      <Option
        key={user.id}
        value={user.id}
        label={`${user.first_name} ${user.last_name}`}
        selectedLabel={
          <>
            {inactiveTag}
            {user.first_name} {user.last_name}
          </>
        }
        className={selectedAndInactive ? "hidden" : ""}
      >
        {inactiveTag}
        <div>
          {user.first_name} {user.last_name}
        </div>
        <div className="text-sm text-gray-500">{user.company.name}</div>
      </Option>
    );
  };

  const shippingLocationDefaultOption = useMemo(() => {
    if (editMaterialData?.place_id) {
      return [
        {
          label: formattedSelectedRows[0].shipping_location?.label,
          value: formattedSelectedRows[0].place_id
        }
      ];
    }
    return [];
  }, [formattedSelectedRows, editMaterialData]);

  useEffect(() => {
    if (editMaterialData?.place_id) {
      form.setFieldsValue({
        place_id: editMaterialData.place_id
      });
    }
  }, [editMaterialData?.place_id, form]);

  const onFinish = async () => {
    setInProgress(true);
    try {
      const tmpInitialData = { ...initialData };
      delete tmpInitialData.location_ids;

      const formData: any = { ...editMaterialData } as any;

      const compare = compareObjectPropertyValues(
        [tmpInitialData, editMaterialData],
        Object.getOwnPropertyNames(tmpInitialData).map((x) => ({
          field: x
        }))
      );

      const changedFields: string[] = Object.keys(formData).filter(
        (field: string) => {
          return compare.fieldsAsObject[field]?.diffFound;
        }
      );
      const changedFieldsObject = changedFields.reduce(
        (accu: any, key: string) => {
          const tmpAccu = { ...accu };
          tmpAccu[key] = formData[key] || null;
          return tmpAccu;
        },
        {}
      );

      if (isLBSFeatureEnabled) {
        // when LBS enabled, location_ids is always set to an array in the init effect -> `|| []` fallbacks are dead
        /* v8 ignore next 4 */
        const compareForLocation = compareStringArrays(
          initialData?.location_ids || [],
          editMaterialData.location_ids || []
        );
        if (!compareForLocation.isSame) {
          // formData.location_ids mirrors editMaterialData (always an array under LBS) -> `|| []` fallback is dead
          /* v8 ignore next */
          changedFieldsObject.location_ids = formData.location_ids || [];
        }
      }

      if (Object.keys(customFieldChanges).length > 0) {
        changedFieldsObject._custom_fields = customFieldChanges;
      }

      const bulkUpdatePayload = {
        feature_instance_type_id: FeatureTypes.MATERIALS,
        feature_instance_ids: formattedSelectedRows.map((item) => item.id),
        updates: {
          ...changedFieldsObject
        }
      };

      const bulkUpdateResults = await bulkUpdateFeatureInstances({
        variables: bulkUpdatePayload
      });

      if (bulkUpdateResults.data) {
        message.success("Materials updated successfully");
        setTimeout(() => {
          onUpdateDone();
        }, 0);
      }
    } catch (err) {
      console.error(err);
      message.error(err as any);
    }
    setInProgress(false);
  };

  return (
    <Modal
      title="Bulk Edit Details"
      className="custom-drawer"
      style={SidepanelStyles.style}
      width={420}
      footer={[
        <div className="flex w-full space-x-3 justify-end" key={100}>
          <Button
            onClick={onCancelClick}
            key={101}
            disabled={inProgress}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            disabled={!isDiff || inProgress}
            loading={inProgress}
            onClick={onFinish}
            key={102}
            data-testid="apply-button"
          >
            Apply
          </Button>
        </div>
      ]}
      open={showEdit}
      onCancel={() => {
        setShowEdit(false);
      }}
      destroyOnHidden
    >
      <div className="body-content-wrapper">
        <div
          className=" mb-3 px-2 font-semibold"
          data-testid="selected-materials-count"
        >
          <span
            className="tracking-wide"
            data-testid="selected-materials-count-text"
          >
            Selected Materials:
          </span>{" "}
          {formattedSelectedRows.length}
        </div>
        <Form
          layout="vertical"
          preserve
          form={form}
          className="space-y-3 min-h-full px-2"
          disabled={showCreateSpecSectionModal}
          data-testid="bulk-edit-form"
        >
          <div className="flex-col overflow-y overflow-x-hidden h-full px-1">
            <Form.Item
              label={materialHeaderMap?.spec_section_no}
              data-testid="spec-section-no-form-item"
            >
              <SelectFeatureSpecSections
                mode="single"
                placeholder="Search Spec Section"
                selectedIds={editMaterialData.spec_sections}
                setSelectedIds={(ids) => {
                  const specIds = ids as string[];
                  setEditMaterialData((pre) => ({
                    ...pre,
                    spec_sections: specIds
                  }));
                }}
                onCreateSpecSectionClick={(showSpecSectionView: boolean) => {
                  setShowCreateSpecSectionModal(showSpecSectionView);
                  setTimeout(() => {
                    if (createSpecRef.current)
                      createSpecRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "end"
                      });
                  }, 100);
                }}
              />
            </Form.Item>
            <Form.Item
              label={materialHeaderMap?.purchase_order}
              data-testid="purchase-order-form-item"
            >
              <Input
                placeholder="Enter Purchase Order"
                value={editMaterialData.purchase_order}
                onChange={(event: any) => {
                  setEditMaterialData((prev: any) => {
                    return {
                      ...prev,
                      purchase_order: event.target.value
                    };
                  });
                }}
                data-testid="purchase-order-input"
              />
            </Form.Item>
            <Form.Item
              label={materialHeaderMap?.tag_name}
              data-testid="tag-name-form-item"
            >
              <Input
                placeholder="Enter Material Tag"
                value={editMaterialData.tag_name}
                onChange={(event: any) => {
                  setEditMaterialData((prev: any) => {
                    return {
                      ...prev,
                      tag_name: event.target.value
                    };
                  });
                }}
                data-testid="tag-name-input"
              />
            </Form.Item>
            <Form.Item
              label={materialHeaderMap?.description}
              data-testid="description-form-item"
            >
              <TextArea
                rows={4}
                placeholder="Enter material description"
                value={editMaterialData.description}
                onChange={(event: any) => {
                  setEditMaterialData((prev: any) => {
                    return {
                      ...prev,
                      description: event.target.value
                    };
                  });
                }}
                data-testid="description-input"
              />
            </Form.Item>
            {isLBSFeatureEnabled && (
              <Form.Item
                label={
                  <div className="flex justify-between items-center w-full pt-[5px]">
                    <span>{materialHeaderMap?.locations}</span>
                    <Tooltip title="Open Manage Locations">
                      <Link
                        className="flex linking-container--nav__redirectlinkIconLocation w-[80px] text-xs"
                        to={`/project/${projectId}/settings/locations`}
                        target="_blank"
                        data-testid="bulkfeatureeditmaterialdetails-link"
                      >
                        <div className="font-normal text-black">Manage</div>
                        <div className="ml-1 pt-[2px]">
                          <RedirectIcon size={14} />
                        </div>
                      </Link>
                    </Tooltip>
                  </div>
                }
              >
                <LBSSelectField
                  LBSOptionsMaster={LBSOptionsMaster}
                  LBSDataInTreeFormat={LBSDataInTreeFormat}
                  onChange={(selectedIds: string[]) => {
                    setEditMaterialData((pre) => ({
                      ...pre,
                      location_ids: selectedIds
                    }));
                  }}
                  initialValues={editMaterialData.location_ids || []}
                  projectId={projectId}
                  data-testid="location-select-field"
                />
              </Form.Item>
            )}

            {materialTemplateSelectField}
            <Form.Item
              label={materialHeaderMap?.trade_partner}
              data-testid="trade-partner-form-item"
            >
              <Select
                showSearch
                value={editMaterialData.responsible_contractor}
                optionFilterProp="label"
                className="constructionSelect"
                onChange={(value) => {
                  setEditMaterialData((pre) => ({
                    ...pre,
                    responsible_contractor: value,
                    assignee: undefined,
                    assignee_unregistered: undefined
                  }));
                }}
                notFoundContent={
                  <SelectSearchNotFoundContent
                    notFoundMsg={
                      projectParticipants.materialTradePartners?.length > 0
                        ? "Company not found. To add, go to "
                        : "No companies added to this project. To add, go to "
                    }
                    linkTitle="Project Companies"
                    linkPath={`/project/${projectId}/settings/general/project-companies`}
                  />
                }
                data-testid="trade-partner-dropdown"
                labelRender={(labelProps) => (
                  <span data-testid="trade-partner-value">
                    {labelProps.label}
                  </span>
                )}
                options={projectParticipants.materialTradePartners.map(
                  (item: any) => ({
                    value: item.vendor_id,
                    label: item.subscription_vendor.name
                  })
                )}
              />
            </Form.Item>
            <Tooltip
              title={
                editMaterialData.responsible_contractor
                  ? ""
                  : "First select a Responsible Contractor"
              }
              placement="rightBottom"
            >
              <Form.Item
                label={materialHeaderMap?.assignee}
                data-testid="assignee-form-item"
              >
                <Select
                  data-testid="assignee-dropdown"
                  labelRender={(labelProps) => (
                    <span data-testid="assignee-value">{labelProps.label}</span>
                  )}
                  showSearch
                  optionFilterProp="label"
                  // antd v6 renders the Option's children as the *selected*
                  // value, so the company sub-line ended up inside the closed
                  // Select. Point the selected value at a name-only node; the
                  // dropdown list still renders the children, sub-line included.
                  optionLabelProp="selectedLabel"
                  className="constructionSelect"
                  style={{ minHeight: 32 }}
                  value={
                    editMaterialData.assignee ||
                    editMaterialData.assignee_unregistered
                  }
                  onChange={(value) => {
                    const selectedUser =
                      projectParticipants?.submitterUsers.find(
                        (user: any) => user.id === value
                      );

                    if (selectedUser.type === "actual") {
                      setEditMaterialData((pre: any) => ({
                        ...pre,
                        assignee: value,
                        assignee_unregistered: undefined
                      }));
                    } else {
                      setEditMaterialData((pre: any) => ({
                        ...pre,
                        assignee: null,
                        assignee_unregistered: value
                      }));
                    }
                  }}
                  disabled={!editMaterialData.responsible_contractor}
                >
                  {projectParticipants?.submitterUsers
                    .filter(
                      (user: any) =>
                        user.company.vendor_id ===
                        editMaterialData.responsible_contractor
                    )
                    .map((user: any) =>
                      optionLabel(user, editMaterialData.assignee)
                    )}
                </Select>
              </Form.Item>
            </Tooltip>
            <Form.Item
              label={materialHeaderMap?.gc_representative}
              data-testid="gc-representative-form-item"
            >
              <Select
                data-testid="gc-representative-dropdown"
                labelRender={(labelProps) => (
                  <span data-testid="gc-representative-value">
                    {labelProps.label}
                  </span>
                )}
                showSearch
                filterOption
                optionFilterProp="label"
                // antd v6 renders the Option's children as the *selected*
                // value, so the company sub-line ended up inside the closed
                // Select. Point the selected value at a name-only node; the
                // dropdown list still renders the children, sub-line included.
                optionLabelProp="selectedLabel"
                value={editMaterialData.gc_representative}
                onChange={(value) => {
                  setEditMaterialData((pre) => ({
                    ...pre,
                    gc_representative: value
                  }));
                }}
                notFoundContent="User not found. Go to Project Settings to add users"
              >
                {projectParticipants?.gcReviewers.map((user: any) =>
                  optionLabel(user, editMaterialData.gc_representative)
                )}
              </Select>
            </Form.Item>
            <Form.Item
              label={materialHeaderMap?.shipping_location}
              data-testid="shipping-location-form-item"
              name="place_id"
            >
              <Select
                placeholder="Search by Address, City or State"
                showSearch
                allowClear
                filterOption={false}
                loading={fetching}
                options={
                  searchedString
                    ? shippingLocationOptionResults
                    : shippingLocationDefaultOption
                }
                onSearch={searchLocation}
                notFoundContent={
                  <LocationSearchNotFoundMessages
                    searchedString={searchedString}
                    fetching={fetching}
                  />
                }
                onChange={(value) => {
                  setEditMaterialData((prev: any) => {
                    return {
                      ...prev,
                      place_id: value
                    };
                  });
                }}
              />
            </Form.Item>
            <Form.Item
              label={materialHeaderMap?.manufacturer}
              data-testid="manufacturer-form-item"
            >
              <Input
                placeholder="Enter Manufacturer"
                value={editMaterialData.manufacturer}
                onChange={(event: any) => {
                  setEditMaterialData((prev: any) => {
                    return {
                      ...prev,
                      manufacturer: event.target.value
                    };
                  });
                }}
                data-testid="manufacturer-input"
              />
            </Form.Item>
            <Form.Item
              label={materialHeaderMap?.part_number}
              data-testid="part-number-form-item"
            >
              <Input
                placeholder="Enter part number"
                value={editMaterialData.part_number}
                onChange={(event: any) => {
                  setEditMaterialData((prev: any) => {
                    return {
                      ...prev,
                      part_number: event.target.value
                    };
                  });
                }}
                data-testid="part-number-input"
              />
            </Form.Item>
            <div className="grid grid-cols-2 gap-x-2">
              <Form.Item
                label={materialHeaderMap?.material_size}
                data-testid="material-size-form-item"
              >
                <Input
                  placeholder="Enter Material Size"
                  className="w-full"
                  value={editMaterialData.material_size}
                  onChange={(event: any) => {
                    setEditMaterialData((prev: any) => {
                      return {
                        ...prev,
                        material_size: event.target.value
                      };
                    });
                  }}
                  data-testid="material-size-input"
                />
              </Form.Item>
              <Form.Item
                label={materialHeaderMap?.size_unit_id}
                data-testid="size-unit-form-item"
              >
                <TreeSelect
                  data-testid="size-unit-dropdown"
                  placeholder="Enter Material Size Unit"
                  value={editMaterialData.size_unit_id}
                  treeData={
                    allUnits
                      ? getTreeFromMeasuremntList(allUnits.material_size_unit)
                      : []
                  }
                  onChange={(value: any) => {
                    const data = allUnits?.material_size_unit.find(
                      (x) => x.id === value
                    );
                    setEditMaterialData((prev: any) => ({
                      ...prev,
                      size_unit_id: data?.id
                    }));
                  }}
                  treeDefaultExpandAll
                />
              </Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-2">
              <Form.Item
                label={materialHeaderMap?.quantity}
                data-testid="quantity-form-item"
              >
                <Input
                  type="number"
                  value={editMaterialData.quantity}
                  min={0}
                  placeholder="Enter Quantity"
                  className="w-full"
                  onChange={(e) => {
                    setEditMaterialData((pre) => ({
                      ...pre,
                      quantity: e.target.value
                    }));
                  }}
                  data-testid="quantity-input"
                />
              </Form.Item>
              <Form.Item
                label={materialHeaderMap?.quantity_unit_id}
                data-testid="quantity-unit-form-item"
              >
                <TreeSelect
                  data-testid="quantity-unit-dropdown"
                  treeNodeLabelProp="label"
                  placeholder="Enter Quantity Unit"
                  value={editMaterialData.quantity_unit_id}
                  treeData={quantityUnitTreeOptions}
                  onChange={(value: any) => {
                    setEditMaterialData((prev: any) => ({
                      ...prev,
                      quantity_unit_id: value
                    }));
                  }}
                  treeDefaultExpandAll
                />
              </Form.Item>
            </div>
            {materialCustomColumns?.length > 0 && (
              <Form.Item>
                <CustomFields
                  featureInstanceData={syntheticFeatureInstanceData}
                  data-testid="submittal-bulk-edit-others"
                  type={FeatureTypes.MATERIALS}
                  columns={materialCustomColumns}
                  onChange={onCustomFieldChange}
                  className="space-y-3"
                />
              </Form.Item>
            )}
          </div>
        </Form>

        {showCreateSpecSectionModal && (
          <div ref={createSpecRef} className="pb-3">
            <Divider style={{ margin: "15px 0" }} />
            <div>
              <CreateSpecSectionModal
                isModalOpen={showCreateSpecSectionModal}
                onDoneCb={(newSpecSectionId: string) => {
                  if (newSpecSectionId) {
                    setEditMaterialData((pre) => ({
                      ...pre,
                      spec_sections: [newSpecSectionId]
                    }));
                  }
                  setShowCreateSpecSectionModal(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
export default BulkFeatureEditMaterialDetails;
